/* eslint-disable @typescript-eslint/no-loop-func */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  useContext, useEffect, useRef, useState,
} from 'react';

import { FormContext } from '../Form/Form';
import { IFormContext } from '../../interfaces/components/form.interface';

import { useModal } from '../../contexts/Modal';
import { IProps } from '../../interfaces/components/virtualKeyVehicles.interface';
import errorHandler from '../../services/sentry';
import { SelectionsContext } from '../../contexts/Selections';
import VehicleCard from '../VehicleCard';
import { useFlashErrorText } from '../../contexts/FlashErrorText';
import api from '../../services/api';

interface IVehiclesForm {
  vehicles: {
    [key: string]: {
      value: boolean;
      error?: string;
    };
  };
}

const VirtualKeyVehicles = ({
  statuses,
  errorMap,
  vehicles,
}: IProps) => {
  const context = useContext<IFormContext | undefined>(FormContext);
  const selectionsContext = useContext(SelectionsContext);
  const { view: modalView, openModal, closeModal } = useModal();
  const [
    keyCheckOperation, setKeyCheckOperation,
  ] = useState<() => Promise<void> | null>(() => null);
  const { setFlashErrorText } = useFlashErrorText();
  const abortControllerRef = useRef<AbortController | null>(null);

  const handlePairingSuccess = (vehicleId) => {
    context?.setFormState((formState: IVehiclesForm) => ({
      ...formState,
      vehicles: {
        ...formState.vehicles,
        [vehicleId]: {
          value: true,
          error: undefined,
        },
      },
    }));
    setFlashErrorText('');
  };

  const createKeyCheckOperation = (vehicle) => async () => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const { data } = await api.getComponents(
        `/vehicle/${vehicle.id}/virtual-key-status`,
        true,
        { signal: abortController.signal },
      );

      if (data.status === 'success') {
        handlePairingSuccess(vehicle.id);
      } else {
        throw new Error('pairingFailed');
      }
    } catch (err: any) {
      // don't treat user cancelation as an error
      if (err.message === 'canceled') {
        return;
      }
      // don't report errors where vehicle pairing status is false
      if (err.message !== 'pairingFailed') {
        errorHandler(err);
      }
      const message = errorMap[err.message] || errorMap.default;
      context?.setFormState((formState: IVehiclesForm) => ({
        ...formState,
        vehicles: {
          ...formState.vehicles,
          [vehicle.id]: {
            ...formState.vehicles[vehicle.id],
            error: message,
          },
        },
      }));
      setFlashErrorText(message);
    } finally {
      closeModal();
      abortControllerRef.current = null;
    }
  };

  const createKeyPairingHandler = (vehicle) => async () => {
    // confirm with precheck before initiating pairing steps
    const { data } = await api.getComponents(
      `/vehicle/${vehicle.id}/virtual-key-status?isPreCheck=true`,
    );
    if (data.status === 'success') {
      handlePairingSuccess(vehicle.id);
    } else {
      selectionsContext?.setVehicleToPair(vehicle);
      setFlashErrorText('');
      openModal('setupVirtualKey');
      setKeyCheckOperation(() => createKeyCheckOperation(vehicle));
    }
  };

  const cancelOperation = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  useEffect(() => {
    return closeModal;
  }, []);

  useEffect(() => {
    if (modalView === 'checkingVirtualKey' && keyCheckOperation) {
      keyCheckOperation();
    } else if (modalView !== 'checkingVirtualKey') {
      cancelOperation();
    }
  }, [modalView, keyCheckOperation]);

  return (context?.formState as IVehiclesForm).vehicles ? vehicles.map((vehicle, i) => {
    const { virtualKeyRequirement } = vehicle;
    const granted = Boolean(
      (context?.formState as IVehiclesForm)?.vehicles[vehicle.id]?.value,
    );
    const errored = Boolean(
      (context?.formState as IVehiclesForm)?.vehicles[vehicle.id]?.error,
    );
    const chipAdditionaClassNames = (virtualKeyRequirement === 'notRequired' && 'not-required')
      || (granted && 'granted')
      || (errored && 'error')
      || 'required';

    const statusLabel = statuses[
      (virtualKeyRequirement === 'notRequired' && 'notRequired')
      || (granted && 'granted')
      || (errored && 'errored')
      || 'required'
    ];
    const lastVehicle = i === vehicles.length - 1;
    return (
      <VehicleCard
        key={vehicle.id}
        disabled={Boolean(granted)}
        vehicle={vehicle}
        statusLabel={statusLabel}
        handleClick={createKeyPairingHandler(vehicle)}
        chipAdditionaClassNames={chipAdditionaClassNames}
        additionalClassNames={lastVehicle ? 'm-b-xl' : ''}
      />
    );
  }) : null;
};

export default VirtualKeyVehicles;
